/**
 * G-27 kit: dictate into a text field. Put one beside any textarea a florist
 * fills in with wet hands.
 *
 * RENDERS NOTHING outside the tablet app (interaction rule 5). There is no
 * browser fallback and no disabled state standing in for one — a desktop user
 * has their operating system's dictation, and this repo has a history of dead
 * florist-facing buttons (G-15 removed one).
 *
 * Owns its own recogniser session through `useDictation`, so a consumer needs
 * two lines: the button, and the state it writes into. Do NOT put two of these
 * on one screen expecting both to listen — the native side allows one session at
 * a time by construction, and the second would be told it is BUSY.
 *
 * The language is a PROP, not internal state: on most screens it follows the
 * florist, but on the gift-note screen the words belong to a customer and the
 * screen must be able to say so. Whoever renders the button decides.
 */

import { useRef } from "react";
import {
  DICTATION_LOCALES,
  type DictationLocale,
} from "../../platform/dictation";
import { useT } from "../../i18n/LanguageProvider";
import { appendDictated } from "./dictationText";
import { useDictation } from "./useDictation";

/** 24px, stroke-only, 1.8 weight — the house icon idiom (G-65 slice 3). */
function MicGlyph() {
  return (
    <svg
      className="kit-dictate-glyph"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

export function DictationButton({
  value,
  onChange,
  locale,
  ariaLabel,
  maxLength,
  disabled = false,
}: {
  value: string;
  onChange: (next: string) => void;
  locale: DictationLocale;
  /** Names the field this dictates into, for a screen reader. */
  ariaLabel: string;
  /** The field's own cap. Enforced here because `maxLength` ignores code. */
  maxLength?: number;
  disabled?: boolean;
}) {
  /**
   * The field's current text, tracked in a ref as well as in props.
   *
   * NOT belt-and-braces — load-bearing. `/code-review` 2026-09-12 caught the
   * first version reading `value` straight from the closure: Vosk emits a result
   * at every pause, and two results arriving before React re-renders would both
   * append to the SAME stale base string, so the earlier utterance was silently
   * overwritten. That is precisely the append-never-replace failure interaction
   * rule 1 exists to prevent, and a florist would only notice by re-reading the
   * card after it printed.
   *
   * Updated during render so it tracks the parent, and again inside `onText` so
   * back-to-back results chain onto each other instead of racing.
   */
  const valueRef = useRef(value);
  valueRef.current = value;

  const { t } = useT();
  const { supported, status, interim, error, start, stop } = useDictation({
    onText: (text) => {
      const next = appendDictated(valueRef.current, text, maxLength);
      valueRef.current = next;
      onChange(next);
    },
  });

  if (!supported) return null;

  const listening = status === "listening";
  const starting = status === "starting";
  // Both states stop on a tap, but only ONE of them may look live.
  const active = listening || starting;
  const label = DICTATION_LOCALES.find((l) => l.code === locale)?.label ?? locale;

  return (
    <div className="kit-dictate">
      <button
        type="button"
        className={listening ? "kit-dictate-button is-live" : "kit-dictate-button"}
        onClick={() => void (active ? stop() : start(locale))}
        disabled={disabled}
        aria-pressed={active}
        aria-label={
          listening
            ? t("dictate.stopField", { field: ariaLabel })
            : t("dictate.fieldInLanguage", { field: ariaLabel, language: label })
        }
      >
        <MicGlyph />
        {/* The language is on the button itself rather than behind a tap.
            A florist can see which model will hear them BEFORE they speak,
            which is the whole value a confirmation dialog would have added —
            without the tap, and without being tapped through unread by day two. */}
        <span className="kit-dictate-lang">{label}</span>
      </button>

      {/* Interim words prove it is listening, and they are the real check that
          the right language is loaded: the wrong model produces nonsense in the
          first two words, which is faster and more honest than any dialog.

          "Getting ready" is not decoration — on a first use of a language the
          model is still being unpacked, and telling a florist to speak then
          would lose everything they said. */}
      {starting ? (
        <p className="kit-dictate-interim" aria-live="polite">
          {t("dictate.gettingReady")}
        </p>
      ) : listening ? (
        <p className="kit-dictate-interim" aria-live="polite">
          {interim.length > 0 ? interim : "Listening…"}
        </p>
      ) : null}

      {error !== null && !active ? (
        <p className="kit-dictate-error" role="status">
          {error}
        </p>
      ) : null}
    </div>
  );
}
